import React, { useState, useMemo } from "react";
import { Link } from "react-router-dom";
import { Calendar, Clock, MapPin, Tag, ArrowRight } from "lucide-react";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "../components/ui/dialog";
import { useToast } from "../hooks/use-toast";
import Reveal from "../components/Reveal";
import PaymentQRs from "../components/PaymentQRs";
import { events, pressFlyer } from "../mock";

const categories = ["All", "Networking", "Community", "Workshop", "Fundraiser", "Program"];

const fmtFull = (d) =>
  new Date(d + "T00:00:00").toLocaleDateString("en-US", { weekday: "long", month: "long", day: "numeric", year: "numeric" });

const Events = () => {
  const { toast } = useToast();
  const [filter, setFilter] = useState("All");
  const [active, setActive] = useState(null);

  const featured = events.find((e) => e.featured);

  const filtered = useMemo(() => {
    const rest = events.filter((e) => !e.featured);
    return filter === "All" ? rest : rest.filter((e) => e.category === filter);
  }, [filter]);

  const rsvp = () => {
    toast({ title: "You're on the list! 🎊", description: `See you at ${active?.title}.` });
    setActive(null);
  };

  return (
    <div>
      <section className="pt-32 lg:pt-40 pb-16 bg-secondary/40 relative overflow-hidden">
        <div className="absolute -top-20 right-10 h-72 w-72 rounded-full bg-accent/10 blur-3xl" />
        <div className="container-prc relative text-center max-w-3xl mx-auto">
          <Badge className="bg-white text-primary hover:bg-white rounded-full px-4 py-1.5 font-semibold mb-5">Events</Badge>
          <h1 className="font-display font-extrabold text-4xl lg:text-5xl text-foreground text-balance">Stronger together for children, families, &amp; the future</h1>
        </div>
      </section>

      {featured && (
        <section className="py-14">
          <div className="container-prc">
            <Reveal className="grid lg:grid-cols-2 gap-8 items-center bg-white rounded-[2rem] shadow-lift overflow-hidden">
              <div className="p-3 sm:p-5 bg-secondary/30">
                <div className="rounded-2xl overflow-hidden bg-white">
                  <img src={pressFlyer} alt={featured.title} className="w-full block" />
                </div>
              </div>
              <div className="p-8 lg:pr-12">
                <div className="flex flex-wrap items-center gap-2 mb-4">
                  <Badge className="bg-accent/10 text-accent hover:bg-accent/10 rounded-full font-semibold">Save the Date</Badge>
                  <Badge className="bg-primary text-primary-foreground hover:bg-primary rounded-full font-semibold">Registration: {featured.price}</Badge>
                </div>
                <h2 className="font-display font-extrabold text-2xl lg:text-3xl text-foreground leading-tight">{featured.title}</h2>
                {featured.tagline && (
                  <p className="mt-3 italic text-primary/80">“{featured.tagline}”</p>
                )}
                <div className="mt-5 space-y-2.5 text-foreground/80">
                  <p className="flex items-center gap-2.5"><Calendar className="h-5 w-5 text-primary shrink-0" /> {fmtFull(featured.date)}</p>
                  <p className="flex items-center gap-2.5"><Clock className="h-5 w-5 text-primary shrink-0" /> {featured.time}</p>
                  <p className="flex items-start gap-2.5"><MapPin className="h-5 w-5 text-primary shrink-0 mt-0.5" /> {featured.location}</p>
                </div>
                <p className="mt-5 text-muted-foreground leading-relaxed">{featured.description}</p>
                {featured.highlights && (
                  <div className="mt-5 flex flex-wrap gap-2">
                    {featured.highlights.map((h) => (
                      <span key={h} className="px-3 py-1.5 rounded-full bg-secondary text-secondary-foreground text-xs font-semibold">{h}</span>
                    ))}
                  </div>
                )}
                <Button asChild className="btn-cta mt-7 bg-accent hover:bg-accent/90 text-accent-foreground rounded-full font-bold px-7 h-12">
                  <Link to="/register">Register Now · {featured.price} <ArrowRight className="h-5 w-5 ml-2" /></Link>
                </Button>
              </div>
            </Reveal>
          </div>
        </section>
      )}

      <section className="py-14">
        <div className="container-prc">
          <div className="flex flex-wrap justify-center gap-3 mb-12">
            {categories.map((c) => (
              <button
                key={c}
                onClick={() => setFilter(c)}
                className={`px-5 py-2.5 rounded-full text-sm font-bold transition-colors ${
                  filter === c ? "bg-primary text-primary-foreground" : "bg-white text-foreground/70 hover:text-primary shadow-soft"
                }`}
              >
                {c}
              </button>
            ))}
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {filtered.map((ev, i) => (
              <Reveal key={ev.id} delay={(i % 3) * 80}>
                <div className="card-hover bg-white rounded-2xl overflow-hidden shadow-soft h-full flex flex-col">
                  <div className="img-zoom h-48 relative">
                    <img src={ev.image} alt={ev.title} className="w-full h-full object-cover" />
                    <Badge className="absolute top-3 left-3 bg-accent text-accent-foreground hover:bg-accent rounded-full font-semibold">{ev.category}</Badge>
                    <span className="absolute top-3 right-3 bg-white rounded-full px-3 py-1 text-xs font-bold text-primary">{ev.price}</span>
                  </div>
                  <div className="p-6 flex flex-col flex-1">
                    <h3 className="font-display font-bold text-xl text-foreground">{ev.title}</h3>
                    <p className="mt-2 text-sm text-muted-foreground flex-1">{ev.description}</p>
                    <div className="mt-4 space-y-2 text-sm text-foreground/70">
                      <p className="flex items-center gap-2"><Calendar className="h-4 w-4 text-primary" /> {fmtFull(ev.date)}</p>
                      <p className="flex items-center gap-2"><Clock className="h-4 w-4 text-primary" /> {ev.time}</p>
                      <p className="flex items-center gap-2"><MapPin className="h-4 w-4 text-primary" /> {ev.location}</p>
                    </div>
                    <Button onClick={() => setActive(ev)} className="btn-cta mt-5 bg-primary hover:bg-primary/90 rounded-full font-bold">
                      RSVP <ArrowRight className="h-4 w-4 ml-2" />
                    </Button>
                  </div>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <PaymentQRs subtitle={`Scan a code to pay your ${featured ? featured.price : "$49"} registration via Zelle, Venmo, or Cash App.`} />

      <Dialog open={!!active} onOpenChange={(o) => !o && setActive(null)}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="font-display text-xl">RSVP — {active?.title}</DialogTitle>
            <DialogDescription className="flex items-center gap-2"><Tag className="h-4 w-4" /> {active?.price} • {active && fmtFull(active.date)}</DialogDescription>
          </DialogHeader>
          <div className="space-y-3 pt-2">
            <input className="w-full h-11 rounded-xl border border-input px-4 text-sm" placeholder="Full name" />
            <input className="w-full h-11 rounded-xl border border-input px-4 text-sm" placeholder="Email address" />
            <input className="w-full h-11 rounded-xl border border-input px-4 text-sm" placeholder="Number of guests" type="number" min="1" defaultValue={1} />
            <Button onClick={rsvp} className="btn-cta w-full bg-accent hover:bg-accent/90 text-accent-foreground rounded-full font-bold h-11">Confirm RSVP</Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Events;
